import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Chips,
  Flex,
  Heading,
  Text,
  IconButton,
  AttentionBox,
  Modal,
  ModalHeader,
  ModalContent,
  ModalFooterButtons,
  Tooltip,
} from '@vibe/core';
import { Settings, Delete, Board } from '@vibe/icons';
import SchedulerBoard from '../scheduler/SchedulerBoard';
import FieldMappingWizard from '../settings/FieldMappingWizard';
import { getBoardConfig, isBoardConfigured, listBoardConfigs } from '../../api/board-config.api';
import MultiBoardPicker from '../shared/MultiBoardPicker';
import type { PickedBoard } from '../shared/BoardPicker';
import { useScheduleStore } from '../../stores/scheduleStore';
import mondaySdk from 'monday-sdk-js';

const monday = mondaySdk();

// ─── Types ──────────────────────────────────────────────────────────────────

interface BoardSetupEntry {
  boardId: number;
  boardName: string;
  /** Whether this board's field mapping has been configured */
  configured: boolean;
}

type Phase = 'setup' | 'scheduler';

/** Max board chips shown before collapsing into "+N more" */
const MAX_VISIBLE_CHIPS = 3;

// ─── LocalStorage persistence ───────────────────────────────────────────────

const STORAGE_KEY = 'rp_workspace_scheduler_boards';
/** Set when the user confirms entering the scheduler with zero boards selected
 *  ("Open anyway" in the confirmation dialog). Lets us restore the leaves-only
 *  view on subsequent loads instead of bouncing them back to the setup page. */
const NO_BOARDS_CONFIRMED_KEY = 'rp_workspace_scheduler_no_boards_confirmed';

function loadSavedBoards(): BoardSetupEntry[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return JSON.parse(stored) as BoardSetupEntry[];
  } catch { /* ignore */ }
  return [];
}

function saveBoardsToStorage(boards: BoardSetupEntry[]): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(boards));
}

function loadNoBoardsConfirmed(): boolean {
  return localStorage.getItem(NO_BOARDS_CONFIRMED_KEY) === '1';
}

function setNoBoardsConfirmed(value: boolean): void {
  if (value) localStorage.setItem(NO_BOARDS_CONFIRMED_KEY, '1');
  else localStorage.removeItem(NO_BOARDS_CONFIRMED_KEY);
}

// ─── Component ──────────────────────────────────────────────────────────────

const WorkspaceScheduler: React.FC = () => {
  // Boards the user has added for the multi-board scheduler
  const [setupBoards, setSetupBoards] = useState<BoardSetupEntry[]>(() => loadSavedBoards());

  // Which board is currently being configured via field mapping wizard
  const [configuringBoardId, setConfiguringBoardId] = useState<number | null>(null);

  // Current phase: 'setup' (board selection) or 'scheduler' (viewing the scheduler)
  const [phase, setPhase] = useState<Phase>(() => {
    const saved = loadSavedBoards();
    const allConfigured = saved.length > 0 && saved.every((b) => b.configured);
    // Restore leaves-only mode if the user previously chose to open without boards
    if (saved.length === 0 && loadNoBoardsConfirmed()) return 'scheduler';
    return allConfigured ? 'scheduler' : 'setup';
  });

  // On mount, verify which saved boards already have field mappings configured
  useEffect(() => {
    if (setupBoards.length === 0) return;
    let cancelled = false;

    const verify = async () => {
      const updated = await Promise.all(
        setupBoards.map(async (entry) => {
          try {
            const config = await getBoardConfig(entry.boardId);
            return { ...entry, configured: isBoardConfigured(config) };
          } catch {
            return entry;
          }
        }),
      );
      if (!cancelled) {
        setSetupBoards(updated);
        saveBoardsToStorage(updated);
      }
    };

    verify();
    return () => { cancelled = true; };
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist setup boards whenever they change. If the user adds a board, clear
  // the no-boards-confirmed flag — they've moved past leaves-only mode.
  useEffect(() => {
    saveBoardsToStorage(setupBoards);
    if (setupBoards.length > 0) setNoBoardsConfirmed(false);
  }, [setupBoards]);

  // Sync selected boards from MultiBoardPicker
  const handleBoardsChange = useCallback(
    (picked: PickedBoard[]) => {
      const pickedIds = new Set(picked.map((b) => Number(b.id)));
      const existingIds = new Set(setupBoards.map((b) => b.boardId));

      // Remove boards that were deselected
      const kept = setupBoards.filter((b) => pickedIds.has(b.boardId));

      // Add newly picked boards
      const added: BoardSetupEntry[] = picked
        .filter((b) => !existingIds.has(Number(b.id)))
        .map((b) => ({ boardId: Number(b.id), boardName: b.name, configured: false }));

      const updated = [...kept, ...added];
      setSetupBoards(updated);

      // Verify config for newly added boards
      for (const entry of added) {
        getBoardConfig(entry.boardId)
          .then((config) => {
            if (isBoardConfigured(config)) {
              setSetupBoards((prev) =>
                prev.map((b) => (b.boardId === entry.boardId ? { ...b, configured: true } : b)),
              );
            }
          })
          .catch(() => { /* leave as not configured */ });
      }
    },
    [setupBoards],
  );

  // Remove a board from the setup list
  const handleRemoveBoard = useCallback((boardId: number) => {
    setSetupBoards((prev) => prev.filter((b) => b.boardId !== boardId));
  }, []);

  // When the field mapping wizard completes for a board
  const handleMappingComplete = useCallback(
    (boardId: number) => {
      setSetupBoards((prev) =>
        prev.map((b) => (b.boardId === boardId ? { ...b, configured: true } : b)),
      );
      setConfiguringBoardId(null);
    },
    [],
  );

  // Derive PickedBoard[] for MultiBoardPicker value
  const pickerValue: PickedBoard[] = useMemo(
    () => setupBoards.map((b) => ({ id: String(b.boardId), name: b.boardName })),
    [setupBoards],
  );

  const allConfigured = setupBoards.length > 0 && setupBoards.every((b) => b.configured);
  const activeBoardIds = setupBoards.map((b) => b.boardId);

  // Whether all board chips are expanded (show more)
  const [chipsExpanded, setChipsExpanded] = useState(false);

  // Loading state for "Select all configured boards"
  const [isLoadingConfigured, setIsLoadingConfigured] = useState(false);
  const [selectAllMessage, setSelectAllMessage] = useState<string | null>(null);

  // Confirmation dialog when user opens scheduler with no boards selected
  const [showNoBoardsConfirm, setShowNoBoardsConfirm] = useState(false);

  // Boards that failed to load (populated by useSchedule after a multi-board
  // fetch). Used to visually flag the corresponding chips in the header.
  const brokenBoardIds = useScheduleStore((s) => s.brokenBoardIds);
  const brokenSet = useMemo(() => new Set(brokenBoardIds), [brokenBoardIds]);

  const handleOpenScheduler = useCallback(() => {
    if (setupBoards.length === 0) {
      setShowNoBoardsConfirm(true);
      return;
    }
    setPhase('scheduler');
  }, [setupBoards.length]);

  /** Fetch all pre-configured boards and add them to the setup list */
  const handleSelectAllConfigured = useCallback(async () => {
    setIsLoadingConfigured(true);
    setSelectAllMessage(null);
    try {
      // Fetch all configured boards (max 150 per page — enough for most accounts)
      const result = await listBoardConfigs(1, 150);
      if (result.items.length === 0) {
        setSelectAllMessage('No boards found in configuration. Add a board and configure its field mappings first.');
        return;
      }

      // Only include boards whose mapping is complete for their item_level
      // (e.g. a sub-only board doesn't need the main columns populated).
      const configuredItems = result.items.filter(isBoardConfigured);

      if (configuredItems.length === 0) {
        setSelectAllMessage('No boards have complete field mappings yet. Configure Timeline and Assignees columns for your boards first.');
        return;
      }

      const boardIds = configuredItems.map((c) => c.boardId);
      const existingIds = new Set(setupBoards.map((b) => b.boardId));

      // Filter out boards already in the list
      const newBoardIds = boardIds.filter((id) => !existingIds.has(id));
      if (newBoardIds.length === 0) {
        setSelectAllMessage('All configured boards are already selected.');
        return;
      }

      // Fetch board names from monday.com
      const nameMap = new Map<number, string>();
      try {
        const query = `query ($ids: [ID!]!) { boards(ids: $ids) { id name } }`;
        const response = await monday.api(query, {
          variables: { ids: newBoardIds.map(String) },
        });
        const boards = (response.data as { boards?: { id: string; name: string }[] })?.boards;
        if (boards) {
          for (const b of boards) {
            nameMap.set(Number(b.id), b.name);
          }
        }
      } catch {
        // Fall back to generic names
      }

      // Build entries — skip boards that no longer exist in monday.com (deleted boards
      // won't be returned by the GraphQL API, so they won't appear in nameMap).
      // All boards here are already confirmed configured (filtered above).
      const newEntries: BoardSetupEntry[] = newBoardIds
        .filter((id) => nameMap.has(id))
        .map((id) => ({
          boardId: id,
          boardName: nameMap.get(id)!,
          configured: true,
        }));

      setSetupBoards((prev) => [...prev, ...newEntries]);
    } finally {
      setIsLoadingConfigured(false);
    }
  }, [setupBoards]);

  // ── Render: Field mapping wizard for a specific board ────────────────────

  if (configuringBoardId !== null) {
    return (
      <div className="rp-ws-scheduler">
        <div className="rp-ws-scheduler__wizard-header">
          <Button
            kind={Button.kinds.TERTIARY}
            size={Button.sizes.SMALL}
            onClick={() => setConfiguringBoardId(null)}
          >
            Back to board setup
          </Button>
          <Text type={Text.types.TEXT2} style={{ color: '#676879' }}>
            Configuring: {setupBoards.find((b) => b.boardId === configuringBoardId)?.boardName}
          </Text>
        </div>
        <FieldMappingWizard
          boardId={configuringBoardId}
          onComplete={() => handleMappingComplete(configuringBoardId)}
          onSkip={() => setConfiguringBoardId(null)}
        />
      </div>
    );
  }

  // ── Render: Setup phase ─────────────────────────────────────────────────

  if (phase === 'setup') {
    return (
      <div className="rp-ws-scheduler">
        <div className="rp-ws-scheduler__setup">
          <div className="rp-ws-scheduler__setup-header">
            <Heading type={Heading.types.H2}>Resource Scheduler</Heading>
            <Text type={Text.types.TEXT2} style={{ color: '#676879', marginTop: 4, whiteSpace: 'normal', overflow: 'visible', textOverflow: 'unset' }}>
              Plan and track work across one or more boards
              in a single, unified scheduler.<br />
              Select the boards you want to include,
              then map each board's fields so the scheduler
              knows how to read your data.
            </Text>
          </div>

          {/* Board selector */}
          <div className="rp-ws-scheduler__board-selector">
            <Flex align={Flex.align.CENTER} justify={Flex.justify.SPACE_BETWEEN} style={{ marginBottom: 8 }}>
              <Text type={Text.types.TEXT1} weight={Text.weights.BOLD}>
                Add boards
              </Text>
              <Button
                kind={Button.kinds.SECONDARY}
                size={Button.sizes.SMALL}
                onClick={handleSelectAllConfigured}
                disabled={isLoadingConfigured}
                loading={isLoadingConfigured}
              >
                Select all configured boards
              </Button>
            </Flex>
            <MultiBoardPicker
              value={pickerValue}
              onChange={handleBoardsChange}
              placeholder="Search and select boards..."
            />
            <Text
              type={Text.types.TEXT2}
              style={{ color: '#676879', marginTop: 6, fontSize: 12, whiteSpace: 'normal', overflow: 'visible', textOverflow: 'unset' }}
            >
              Only boards you have access to in monday.com are shown — including boards
              from other workspaces, if your monday.com permissions allow it.
            </Text>
            {selectAllMessage && (
              <Text type={Text.types.TEXT2} style={{ color: '#676879', marginTop: 8, fontSize: 13 }}>
                {selectAllMessage}
              </Text>
            )}
          </div>

          {/* Board list with mapping status */}
          {setupBoards.length > 0 && (
            <div className="rp-ws-scheduler__board-list">
              <Flex align={Flex.align.CENTER} justify={Flex.justify.SPACE_BETWEEN} style={{ marginBottom: 8 }}>
                <Text type={Text.types.TEXT1} weight={Text.weights.BOLD}>
                  Selected boards ({setupBoards.length})
                </Text>
                <Button
                  kind={Button.kinds.TERTIARY}
                  size={Button.sizes.SMALL}
                  onClick={() => setSetupBoards([])}
                >
                  Unselect all
                </Button>
              </Flex>
              <div className="rp-ws-scheduler__board-table">
                <div className="rp-ws-scheduler__board-table-header">
                  <div className="rp-ws-scheduler__board-table-cell rp-ws-scheduler__board-table-cell--name">
                    Board name
                  </div>
                  <div className="rp-ws-scheduler__board-table-cell rp-ws-scheduler__board-table-cell--status">
                    Mapping status
                  </div>
                  <div className="rp-ws-scheduler__board-table-cell rp-ws-scheduler__board-table-cell--actions">
                    Actions
                  </div>
                </div>
                {setupBoards.map((board) => (
                  <div key={board.boardId} className="rp-ws-scheduler__board-table-row">
                    <div className="rp-ws-scheduler__board-table-cell rp-ws-scheduler__board-table-cell--name">
                      <Board />
                      <span>{board.boardName}</span>
                    </div>
                    <div className="rp-ws-scheduler__board-table-cell rp-ws-scheduler__board-table-cell--status">
                      {board.configured ? (
                        <span className="rp-ws-scheduler__status rp-ws-scheduler__status--ok">
                          Configured
                        </span>
                      ) : (
                        <span className="rp-ws-scheduler__status rp-ws-scheduler__status--pending">
                          Not configured
                        </span>
                      )}
                    </div>
                    <div className="rp-ws-scheduler__board-table-cell rp-ws-scheduler__board-table-cell--actions">
                      <Button
                        kind={Button.kinds.SECONDARY}
                        size={Button.sizes.SMALL}
                        leftIcon={Settings}
                        onClick={() => setConfiguringBoardId(board.boardId)}
                      >
                        {board.configured ? 'Edit mapping' : 'Configure'}
                      </Button>
                      <IconButton
                        icon={Delete}
                        size={IconButton.sizes.SMALL}
                        kind={IconButton.kinds.TERTIARY}
                        ariaLabel="Remove board"
                        onClick={() => handleRemoveBoard(board.boardId)}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Info box about board configuration */}
          <AttentionBox
            type={AttentionBox.types.PRIMARY}
            title="How board configuration works"
            text={
              'Boards must have field mappings (Timeline and Assignees columns) configured before they can be used in the scheduler. ' +
              'You can pre-configure boards in Settings \u2192 Boards Configuration, or add a board here and click "Configure" to map its fields directly from this page. ' +
              'The "Select all configured boards" button adds every board that already has complete mappings set up, ' +
              'saving you from selecting them one by one.'
            }
          />

          {/* Launch scheduler button */}
          <div className="rp-ws-scheduler__setup-actions">
            <Button
              onClick={handleOpenScheduler}
              disabled={setupBoards.length > 0 && !allConfigured}
              size={Button.sizes.MEDIUM}
            >
              Open Scheduler
            </Button>
            {setupBoards.length > 0 && !allConfigured && (
              <Text type={Text.types.TEXT2} style={{ color: '#e44258', fontSize: 13 }}>
                Please configure field mapping for all selected boards before opening the scheduler.
              </Text>
            )}
          </div>
        </div>

        <Modal
          show={showNoBoardsConfirm}
          onClose={() => setShowNoBoardsConfirm(false)}
          title="Open without boards?"
        >
          <ModalHeader title="Open without boards?" />
          <ModalContent>
            <Text style={{ whiteSpace: 'normal', overflow: 'visible', textOverflow: 'unset', marginTop: 12 }}>
              No boards are selected. You can still open the scheduler to request leaves,
              log work, and see holidays — but you won&apos;t be able to plan work items
              until you add a board.
            </Text>
          </ModalContent>
          <ModalFooterButtons
            primaryButtonText="Open anyway"
            secondaryButtonText="Cancel"
            onPrimaryButtonClick={() => {
              setShowNoBoardsConfirm(false);
              setNoBoardsConfirmed(true);
              setPhase('scheduler');
            }}
            onSecondaryButtonClick={() => setShowNoBoardsConfirm(false)}
          />
        </Modal>
      </div>
    );
  }

  // ── Render: Scheduler phase ─────────────────────────────────────────────

  const noBoards = activeBoardIds.length === 0;

  return (
    <div className="rp-ws-scheduler rp-ws-scheduler--full">
      {/* Compact header — board chips + setup button on one line */}
      <div className="rp-ws-scheduler__header" style={{ padding: '4px 12px', minHeight: 'auto' }}>
        <Flex gap={Flex.gaps.XS} align={Flex.align.CENTER} wrap>
          {noBoards ? (
            <Text type={Text.types.TEXT2} style={{ color: '#676879' }}>
              No boards selected — you can still request leaves and see holidays.
            </Text>
          ) : (
            <>
              {(chipsExpanded ? setupBoards : setupBoards.slice(0, MAX_VISIBLE_CHIPS)).map((board) => {
                const isBroken = brokenSet.has(board.boardId);
                const chip = (
                  <Chips
                    label={isBroken ? `⚠ ${board.boardName}` : board.boardName}
                    readOnly
                    color={isBroken ? Chips.colors.NEGATIVE : Chips.colors.PRIMARY}
                  />
                );
                if (!isBroken) {
                  return <React.Fragment key={board.boardId}>{chip}</React.Fragment>;
                }
                return (
                  <Tooltip
                    key={board.boardId}
                    content={`"${board.boardName}" failed to load. A mapped column (e.g. timeline) may have been removed from the board. Open Board setup to reconfigure this board's field mapping.`}
                    position="bottom"
                    zIndex={10000}
                  >
                    <span style={{ cursor: 'help', display: 'inline-flex' }}>{chip}</span>
                  </Tooltip>
                );
              })}
              {setupBoards.length > MAX_VISIBLE_CHIPS && (
                <div
                  style={{ cursor: 'pointer' }}
                  onClick={() => setChipsExpanded((prev) => !prev)}
                >
                  <Chips
                    label={chipsExpanded ? 'Show less' : `+${setupBoards.length - MAX_VISIBLE_CHIPS} more`}
                    readOnly
                    color={Chips.colors.PRIMARY}
                  />
                </div>
              )}
            </>
          )}
          <Button
            kind={Button.kinds.TERTIARY}
            size={Button.sizes.SMALL}
            leftIcon={Settings}
            onClick={() => setPhase('setup')}
            style={{ marginLeft: 'auto' }}
          >
            Board setup
          </Button>
        </Flex>
      </div>

      {/* Reuse the existing SchedulerBoard with multi-board support */}
      <SchedulerBoard
        boardId={noBoards ? undefined : activeBoardIds[0]}
        multiBoardIds={activeBoardIds}
        multiBoardOptions={setupBoards.map((b) => ({ id: b.boardId, name: b.boardName }))}
      />
    </div>
  );
};

export default WorkspaceScheduler;
